import React, { useCallback } from "react";
import { Calendar, Clock, User } from "lucide-react";
import { BlogPost } from "../../types/blog.types";

interface BlogCardProps {
  post: BlogPost;
  onClick: () => void;
}

export const BlogCard = React.memo(({ post, onClick }: BlogCardProps) => {
  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  const handleClick = useCallback(() => {
    onClick();
  }, [onClick]);

  return (
    <article 
      className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer h-full flex flex-col min-h-[400px]"
      onClick={handleClick}
    >
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
          <div className="flex items-center gap-1">
            <User className="w-4 h-4" />
            <span>{post.author}</span>
          </div>
          
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(post.publishedAt)}</span>
          </div>
          
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{post.readTime} min</span>
          </div>
        </div>
        
        <h3 className="text-lg font-bold text-gray-900 mb-4 line-clamp-2 min-h-[3rem] leading-tight">
          {post.title}
        </h3>
        
        <p className="text-gray-600 mb-6 line-clamp-3 flex-1 text-sm leading-relaxed">
          {post.excerpt}
        </p>
        
        <div className="flex items-center justify-between mt-auto">
          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
            {post.category.replace('-', ' ').toUpperCase()}
          </span>
          
          <div className="flex flex-wrap gap-1">
            {post.tags.slice(0, 2).map((tag, index) => (
              <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                #{tag}
              </span>
            ))}
            {post.tags.length > 2 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                +{post.tags.length - 2}
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
});
